import { Timestamp } from 'firebase/firestore'

export interface CompanyCode {
	code: string
	companyID: string
	companyName: string
	createdByID: string
	createdOn: Timestamp
	isTemp: boolean
	role: Role
	lockEmail: boolean
	userEmail?: string
}

export interface VerificationCode {
	code: string
	companyID: string
	createdOn: Timestamp
	email: string
	userUID: string
	expiresAt?: Timestamp
	needsPassword?: boolean
}

export interface Company {
	active: boolean
	clientCode: string
	logoLink?: string
	color: string
	id: string
	url: string
	createdOn: Timestamp
	legalName: string
	name: string
	supportEmail: string
	disabledPagesByRole: Record<Role, string[]>
	requiredFields?: Partial<Record<Role, readonly (keyof User)[]>>;
	roleLanding?: Partial<Record<Role, string>>;

	shouldAllowPhotos: boolean,
    shouldAllowVideos: boolean,
	daysUntilDeletion?: number
	isBuilderSideTimeLineEnabled?: boolean
	builderTimelineURL?: string
	activeToInactiveAfterDays?: number
	onHoldToActiveAfterDays?: number
	roleAllowedToManipulateFolders?: Role[]
	projectOwnerReminderCadence?: ProjectOwnerReminderCadence
	projectOwnerReminderCadenceUpdatedAt?: Timestamp
	projectOwnerReminderLastSentAt?: Timestamp
}

export enum Role {
	ADMIN = 'ADMIN',
	EMPLOYEE = 'EMPLOYEE',
	ACCOUNT_OWNER = 'ACCOUNTOWNER',
	CLIENT = 'CLIENT',
}

export type ProjectOwnerReminderCadence = 'off' | 'weekly' | 'biweekly' | 'monthly'

export type ConsentSource = 'signup' | 'user-settings' | 'admin' | 'import' | 'adding-company';
export type ConsentMeta = { at: Timestamp; source: ConsentSource };

export interface User {
	createdOn?: Timestamp
	email: string
	firstName: string
	lastName: string
	lastLogin?: Timestamp
	fullName: string
	role: Role
	phoneNumber?: string
	pushToken?: string
	photoURL?: string
	uid: string
	lifeTimePosts?: number
	mustTextStartToEnableSmsAt?: Timestamp
	notificationPreference: 'email' | 'sms'
	adminNotificationOptIn: { email: boolean; sms: boolean }
	transactionalOptIn: { email: boolean; sms: boolean }
	consentMeta?: {
		email?: {
			adminNotification?: ConsentMeta
			transactional?: ConsentMeta
		}
		sms?: {
			adminNotification?: ConsentMeta
			transactional?: ConsentMeta
		}
	}
	verified: boolean
	searchTerms?: string[]
	hasRandomPassword?: boolean
}

export interface Tag {
	lastCreatedByUID?: string
	lastCreatedOn?: Timestamp
	name?: string
	uses?: number
	searchTerms?: string[]
}

export const ProjectStates = {
	Active: 'active',
	Inactive: 'inactive',
	OnHold: 'onHold',
} as const

export type ProjectStates = typeof ProjectStates[keyof typeof ProjectStates]

export interface Project {
	state?: ProjectStates
	numberOfAssets?: number
	createdByUID?: string
	createdOn?: Timestamp
	primary?: {
		fullName?: string
		email?: string
		phoneNumber?: string
		isProjectLinked?: boolean
		linkedUserUID?: string
	}
	secondaryEmails?: string[]
	photoURL?: string
	numberOfPosts?: number
	lastPostCreatedOn?: Timestamp
	projectName: string
	id: string
	notes?: string
	tags?: string[]
	title: string
	updatedByUID?: string
	updatedByName?: string
	updatedOn?: Timestamp
	passwordProtected?: boolean
	isShowcasePublished?: boolean
	timelineLoads?: number
	folderID?: string
	searchTerms?: string[]
}

export type ReportType = 'project' | 'userActivity'
export type ReportStatus = 'queued' | 'running' | 'done' | 'error'
export type ProjectReportStatus = ReportStatus

export interface ProjectReportParams {
	startDate: Timestamp
	endDateExclusive: Timestamp
	states: ProjectStates[]
	folderID?: string
}

export interface UserActivityReportParams {
	startDate: Timestamp
	endDateExclusive: Timestamp
}

interface BaseReport {
	id: string
	companyID: string
	requestedBy: { uid: string; email: string; name?: string }
	status: ReportStatus
	rowCount?: number
	csvPath?: string
	signedUrl?: string
	errorMessage?: string
	createdAt: Timestamp
	updatedAt: Timestamp
	expiresAt?: Timestamp
}

export interface ProjectReport extends BaseReport {
	reportType?: 'project'
	params: ProjectReportParams
}

export interface UserActivityReport extends BaseReport {
	reportType: 'userActivity'
	params: UserActivityReportParams
}

export type CompanyReport = ProjectReport | UserActivityReport

export interface Folder {
	photoURL?: string
	name: string
	id: string
	parentID?: string
}
export interface TimelineProject {
	id?: Project['id']
	state?: ProjectStates
	isPasswordProtected?: boolean
	title: string
	primaryUID?: string
}

export interface TimelineAssetDTO {
	order: number
	url: string
}

export interface TimelinePostDTO {
	assetURLs: TimelineAssetDTO[]
	createdOnMs: number
	id?: string
	title?: string
}

export interface TimelineMetaDTO {
	projectId: string
	state?: ProjectStates
	title: string
}

export interface TimelineDataPayload {
	meta: TimelineMetaDTO
	posts: TimelinePostDTO[]
	updatedAt: number
}

export interface Asset {
	createdByUID?: string
	createdOn?: Timestamp
	link?: string
	id?: string
	path?: string
	projectID?: string
	postID?: string
}

export interface customMeataDataTypes {
	companyID?: string
	projectID?: string
	folderID?: string
	postID?: string
	order?: string
	userToken?: string
	originalName?: string
}

type PostCreatedOn = Timestamp | Date | number | { toMillis?: () => number }

export interface Post {
	createdByUID?: string
	createdOn?: PostCreatedOn
	title?: string
	id?: string
	assetURLs?: { url: string; order: number }[]
	assetIDs?: string[]
	projectID?: string
	updatedByUID?: string
	updatedOn?: Timestamp
}

export interface CompanyAnalytics {
	totalCount?: number
	totalInactiveCount?: number
	lifetimeViews?: number
	pastMonthViews?: number
	previousMonthViews?: number
	projectViews?: { lat: string; lng: string }[]
}

export interface UserAnalytics extends User {
	lastWeek: number
	lastTwoWeeks: number
	lastMonth: number
	lastUpdate?: Timestamp
}

export type UserAnalyticsSortBy = 'fullName' | 'lastWeek' | 'lastTwoWeeks' | 'lastMonth'
export type SortDirection = 'asc' | 'desc'

export interface RetrieveUserAnalyticsOptions {
	sortBy?: UserAnalyticsSortBy
	sort?: SortDirection
	searchTerm?: string
	limitNumber?: number
	offset?: number
}

export interface RetrieveUserAnalyticsResponse {
	payload: UserAnalytics[]
	nextOffset?: number
	totalCount: number
}


export type OwnedProjectRiskBand = 'green' | 'yellow' | 'red' | 'overdue'

export interface OwnedDashboardRiskBreakdown {
	green: number
	yellow: number
	red: number
	overdue: number
}

export interface OwnedDashboardPostTrendPoint {
	dayKey: string
	count: number
}

export interface OwnedDashboardSummaryResponse {
	inactivityWindowDays: number
	ownedActiveCount: number
	ownedOnHoldCount: number
	overdueCount: number
	highRiskCount: number
	postsLast7Days: number
	postsLast30Days: number
	lifetimeTimelineViewsOwned: number
	riskBreakdown: OwnedDashboardRiskBreakdown
	postTrend30d: OwnedDashboardPostTrendPoint[]
}

export interface OwnedProjectDashboardRow {
	projectID: string
	projectName: string
	state: 'active' | 'onHold'
	lastPostCreatedOn?: Timestamp
	daysSinceLastPost?: number
	daysUntilAutoInactive?: number
	riskPercent?: number
	riskBand?: OwnedProjectRiskBand
	timelineLoads: number
	numberOfPosts: number
	updatedOn?: Timestamp
	updatedByName?: string
}

export interface RetrieveOwnedDashboardProjectsPageOptions {
	limitNumber?: number
	offset?: number
}

export interface RetrieveOwnedDashboardProjectsPageResponse {
	payload: OwnedProjectDashboardRow[]
	totalCount: number
	nextOffset?: number
}

export interface userUpdateRecord {
	updatedOn: Timestamp
	updatedByUID: string
	projectID: string
	postID: string
}

export interface PostAssets {
	base64?: string
	order?: number
}

export interface ProjectServiceObject {
	deactivatedByUID?: string
	deactivatedOn?: Timestamp
	projectPassword?: string
}

export interface Task {
	companyID: string
	createdOn: Timestamp,
}

export interface NotificationInput {
	id: string
	title: string
	body: string

	deliveryMethods: ('emailOrSms' | 'inApp')[] // both can be selected

	kind: 'admin' | 'transactional'

	channels?: {
		email?: {
			templateId?: string
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			variables?: Record<string, any>
			subjectOverride?: string
			fromOverride?: string
			replyTo?: string
		}
		// sms intentionally omitted; we use `body` directly
	}

	sendAt: Date // optional scheduling
	persistent?: boolean // for in-app
	expiresAt?: Date  // only if persistent

	action?: {
		label: string
		url: string
	}

	roles: Role[]
	recipientUIDs: string[] // can be 1 or many
	count?: number
	createdOn: Timestamp
	createdByUID: string
}

export interface Notification {
	id: string
	recipientUID: string

	title: string
	body: string

	deliveryMethods: ('emailOrSms' | 'inApp')[]

	kind: 'admin' | 'transactional'

	channels?: {
		email?: {
			templateId?: string
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			variables?: Record<string, any>
			subjectOverride?: string
			fromOverride?: string
			replyTo?: string
		}
		// sms intentionally omitted; we use `body` directly
	}

	// In-App Notification Options
	persistent?: boolean
	expiresAt?: Timestamp
	dismissedAt?: Timestamp
	readAt?: Timestamp
	deletedAt?: Timestamp

	// Optional call-to-action
	action?: {
		label: string
		url: string // can be internal route like '/reports/123'
	}

	sid?: string
	deliveryStatus?: string
	errorCode?: string
	errorMessage?: string
	lastStatusAt?: Timestamp
	createdOn: Timestamp
	createdByID: string
	processedAt?: Timestamp
}


// I need to do some thinkning about this one
export type PeriodScope = 'ytd' | 'quarter';
export interface AutoNotification {
	id: string;
	enabled: boolean;
	name: string;
	title: string;
	targetDaysWithoutPost: number;
	message: string;
	sendTo: {
		primaryHomeBuyer: boolean;                // send to earner
		staffUIDs: string[];             // also notify selected users
	};
	createdAt: Timestamp;
	updatedAt: Timestamp;
	searchTerms: string[]
}
